import { IAdminPatientListItem } from "../../../../@types/admin/AdminPatient";
import { useRefreshWorkflowConfig } from "../../../../hooks/mutations/admin/hospitalDetails";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FieldHint,
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../../shared";

interface IRefreshWorkflowConfigModalProps {
  patient: IAdminPatientListItem;
  onDone: () => void;
}

/**
 * Tells one patient's app to re-read its workflow configuration — Retool's
 * `sendWorkflowPushNotification` (EN-5172).
 *
 * ⚠️ **This is not a message to the patient.** The body is
 * `{ silent: true, data: { MessageType: "Silent", configChanged: "workflow" } }`
 * and Retool's own success toast said "The workflow was refreshed for …". The
 * patient sees nothing.
 *
 * EN-5172's acceptance criterion reads the action as "it sends real
 * notifications to patients ... it must never be one accidental click". That is
 * the query's NAME, not its effect. It still gets a confirmation — Retool fired
 * it straight off a row action, and it does write to a patient's device state —
 * but one that says what actually happens, because an operator told this would
 * notify a patient will avoid a harmless maintenance action.
 */
export function RefreshWorkflowConfigModal({
  patient,
  onDone,
}: IRefreshWorkflowConfigModalProps) {
  const { showToast } = useToast();
  const { refreshWorkflowConfig, isRefreshing } = useRefreshWorkflowConfig();

  const handleSend = async () => {
    try {
      // The USER id, not the patient id — `userId` was added to the list
      // row for this (EN-5172); Retool read it from its own SQL.
      await refreshWorkflowConfig({ userId: patient.userId });
      // Retool's own wording.
      showToast(
        `The workflow was refreshed for ${
          patient.name || `patient ${patient.id}`
        }.`,
        "success"
      );
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          `Something failed when refreshing the workflow for ${
            patient.name || `patient ${patient.id}`
          }.`
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Refresh workflow configuration</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Tell <strong>{patient.name || `patient ${patient.id}`}</strong>&apos;s
          app to re-read its workflow configuration?
        </ModalBodyText>
        <FieldHint>
          A silent push — the patient sees no notification. Use this when a
          workflow has been changed and their app is still acting on the old
          configuration.
        </FieldHint>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isRefreshing}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isRefreshing}
            onClick={handleSend}
          >
            {isRefreshing ? "Refreshing..." : "Refresh workflow"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
